import React, { useState, useEffect } from 'react';
import { database } from '../../supabase';
import './ProductManager.css';

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  images: string[];
  size?: string;
  category?: string;
  stock_quantity: number;
  is_active: boolean;
  tags?: string[];
  created_at: string;
}

interface UploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

interface TempFile {
  file: File;
  previewUrl: string;
}

const ProductManager: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productCategoryFilter, setProductCategoryFilter] = useState<'all' | 'archive' | 'band-tees'>('all');
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    images: [''],
    size: '',
    category: '',
    stock_quantity: '',
    tags: ''
  });
  const [uploadStates, setUploadStates] = useState<Record<number, UploadState>>({});
  const [tempFiles, setTempFiles] = useState<Record<number, TempFile>>({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await database.getAllProducts();
      if (error) throw error;
      
      console.log('Loaded products from database:', data);
      setProducts(data || []);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      images: [''],
      size: '',
      category: '',
      stock_quantity: '',
      tags: ''
    });
    setShowProductForm(true);
  };

  const cleanupTempFiles = () => {
    // Clean up temporary files
    Object.values(tempFiles).forEach(tempFile => {
      URL.revokeObjectURL(tempFile.previewUrl);
    });
    setTempFiles({});
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      images: product.images.length > 0 ? product.images : [''],
      size: product.size || '',
      category: product.category || '',
      stock_quantity: product.stock_quantity.toString(),
      tags: product.tags ? product.tags.join(', ') : ''
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const { error } = await database.deleteProduct(productId);
      if (error) throw error;
      await loadProducts();
    } catch (err) {
      alert('Failed to delete product');
    }
  };

  const handleSaveProduct = async () => {
    try {
      console.log('Starting product save process...');
      console.log('Product form data:', productForm);
      console.log('Temp files:', tempFiles);

      // Upload temporary files first
      await uploadTempFiles();
      console.log('After uploadTempFiles, productForm.images:', productForm.images);

      const productData = {
        name: productForm.name,
        description: productForm.description || null,
        price: parseFloat(productForm.price),
        images: productForm.images.filter(img => img.trim() !== ''),
        size: productForm.size || null,
        category: productForm.category || null,
        stock_quantity: parseInt(productForm.stock_quantity) || 1,
        tags: productForm.tags ? productForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : null
      };

      console.log('Final product data to save:', productData);

      // Check if we have blob URLs (temporary URLs) that need to be uploaded
      const hasBlobUrls = productData.images.some(img => img.startsWith('blob:'));
      if (hasBlobUrls) {
        console.log('WARNING: Product data contains blob URLs, these will not work!');
        console.log('Blob URLs found:', productData.images.filter(img => img.startsWith('blob:')));
        alert('Please upload your images first before saving the product. Blob URLs will not work.');
        return;
      }

      if (editingProduct) {
        console.log('Updating existing product:', editingProduct.id);
        const { error } = await database.updateProduct(editingProduct.id, productData);
        if (error) throw error;
      } else {
        console.log('Creating new product');
        const { error } = await database.createProduct(productData);
        if (error) throw error;
      }

      // Clear temporary files after successful save
      Object.values(tempFiles).forEach(tempFile => {
        URL.revokeObjectURL(tempFile.previewUrl);
      });
      setTempFiles({});

      setShowProductForm(false);
      await loadProducts();
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Failed to save product');
    }
  };

  const addImageField = () => {
    setProductForm(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const updateImageField = (index: number, value: string) => {
    setProductForm(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const removeImageField = (index: number) => {
    if (productForm.images.length > 1) {
      setProductForm(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
      // Remove upload state for this index
      setUploadStates(prev => {
        const newStates = { ...prev };
        delete newStates[index];
        return newStates;
      });
    }
  };

  const handleFileUpload = async (index: number, file: File) => {
    console.log(`handleFileUpload called for index ${index} with file:`, file);
    
    if (!file) {
      console.log('No file provided');
      return;
    }

    console.log(`File details: name=${file.name}, size=${file.size}, type=${file.type}`);

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      console.log('Invalid file type, setting error state');
      setUploadStates(prev => ({
        ...prev,
        [index]: { isUploading: false, progress: 0, error: 'Please upload a valid image file (JPEG, PNG, or GIF)' }
      }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.log('File too large, setting error state');
      setUploadStates(prev => ({
        ...prev,
        [index]: { isUploading: false, progress: 0, error: 'File size must be less than 5MB' }
      }));
      return;
    }

    console.log('File validation passed, creating preview URL');

    // Create temporary preview URL
    const previewUrl = URL.createObjectURL(file);
    console.log('Created preview URL:', previewUrl);
    
    // Store temporary file
    setTempFiles(prev => {
      const newTempFiles = {
        ...prev,
        [index]: { file, previewUrl }
      };
      console.log('Updated tempFiles:', newTempFiles);
      return newTempFiles;
    });

    // Update the image URL in the form with temporary URL
    setProductForm(prev => {
      const newImages = prev.images.map((img, i) => i === index ? previewUrl : img);
      console.log('Updated productForm images:', newImages);
      return {
        ...prev,
        images: newImages
      };
    });

    // Update upload state to success (for temporary preview)
    setUploadStates(prev => ({
      ...prev,
      [index]: { isUploading: false, progress: 100, error: null }
    }));
    
    console.log('handleFileUpload completed successfully');
  };

  // NEW: Simple direct upload function
  const handleDirectUpload = async (index: number, file: File) => {
    console.log(`handleDirectUpload called for index ${index} with file:`, file);
    
    if (!file) {
      console.log('No file provided');
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      console.log('Invalid file type');
      setUploadStates(prev => ({
        ...prev,
        [index]: { isUploading: false, progress: 0, error: 'Please upload a valid image file (JPEG, PNG, or GIF)' }
      }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.log('File too large');
      setUploadStates(prev => ({
        ...prev,
        [index]: { isUploading: false, progress: 0, error: 'File size must be less than 5MB' }
      }));
      return;
    }

    // Set uploading state
    setUploadStates(prev => ({
      ...prev,
      [index]: { isUploading: true, progress: 0, error: null }
    }));

    try {
      console.log(`Uploading file ${index} directly:`, file.name);
      
      const { data, error } = await database.uploadImage(file);
      
      if (error) throw error;
      if (!data) throw new Error('Upload returned no data');

      console.log(`Direct upload successful for ${index}, URL:`, data.publicUrl);

      // Update the image URL in the form with the real Supabase URL
      setProductForm(prev => {
        const newImages = prev.images.map((img, i) => i === index ? data.publicUrl : img);
        console.log('Updated productForm images with Supabase URL:', newImages);
        return {
          ...prev,
          images: newImages
        };
      });

      // Update upload state to success
      setUploadStates(prev => ({
        ...prev,
        [index]: { isUploading: false, progress: 100, error: null }
      }));

    } catch (error) {
      console.error(`Direct upload error for ${index}:`, error);
      setUploadStates(prev => ({
        ...prev,
        [index]: { isUploading: false, progress: 0, error: 'Upload failed. Please try again.' }
      }));
    }
  };

  const uploadTempFiles = async () => {
    console.log('Starting uploadTempFiles with tempFiles:', tempFiles);
    
    const uploadPromises = Object.entries(tempFiles).map(async ([index, tempFile]) => {
      const idx = parseInt(index);
      setUploadStates(prev => ({
        ...prev,
        [idx]: { isUploading: true, progress: 0, error: null }
      }));

      try {
        console.log(`Uploading file ${idx}:`, tempFile.file.name, tempFile.file.size, tempFile.file.type);
        
        const { data, error } = await database.uploadImage(tempFile.file);
        
        console.log(`Upload result for ${idx}:`, { data, error });
        
        if (error) throw error;
        if (!data) throw new Error('Upload returned no data');

        console.log(`Successfully uploaded ${idx}, new URL:`, data.publicUrl);

        // Update the image URL in the form with the real Supabase URL
        setProductForm(prev => {
          const newImages = prev.images.map((img, i) => i === idx ? data.publicUrl : img);
          console.log(`Updated image ${idx} from blob to Supabase URL:`, data.publicUrl);
          return {
            ...prev,
            images: newImages
          };
        });

        // Clean up temporary URL
        URL.revokeObjectURL(tempFile.previewUrl);

        setUploadStates(prev => ({
          ...prev,
          [idx]: { isUploading: false, progress: 100, error: null }
        }));

        return data.publicUrl;
      } catch (error) {
        console.error(`Upload error for ${idx}:`, error);
        setUploadStates(prev => ({
          ...prev,
          [idx]: { isUploading: false, progress: 0, error: 'Upload failed. Please try again.' }
        }));
        return null;
      }
    });

    const results = await Promise.all(uploadPromises);
    console.log('All uploads completed, results:', results);
    
    // Final check: log the final state of productForm.images
    console.log('Final productForm.images after all uploads:', productForm.images);
  };


  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (index: number, e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(index, file);
    }
  };

  const filteredProducts = products.filter(product => {
    if (productCategoryFilter === 'all') return true;
    if (productCategoryFilter === 'archive') return product.category !== 'teeshirts';
    if (productCategoryFilter === 'band-tees') return product.category === 'teeshirts';
    return true;
  });

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="products-section">
      <div className="section-header">
        <h2>Product Management</h2>
        <button className="add-btn" onClick={handleAddProduct}>
          Add New Product
        </button>
      </div>

      <div className="category-filter">
        <h3>Filter by Shop Category</h3>
        <div className="filter-buttons">
          <button
            className={productCategoryFilter === 'all' ? 'active' : ''}
            onClick={() => setProductCategoryFilter('all')}
          >
            All Products
          </button>
          <button
            className={productCategoryFilter === 'archive' ? 'active' : ''}
            onClick={() => setProductCategoryFilter('archive')}
          >
            Archive
          </button>
          <button
            className={productCategoryFilter === 'band-tees' ? 'active' : ''}
            onClick={() => setProductCategoryFilter('band-tees')}
          >
            Band-Tees
          </button>
        </div>
      </div>

      <div className="products-grid">
        {filteredProducts.map(product => (
          <div key={product.id} className="product-card">
            <div className="product-image">
              <img
                src={product.images?.[0] || '/images/placeholder.jpg'}
                alt={product.name}
                onError={(e) => {
                  console.log('Image failed to load for product:', product.name, 'URL:', product.images?.[0]);
                  console.log('Full product object:', product);
                  e.currentTarget.src = '/images/placeholder.jpg';
                }}
                onLoad={(e) => {
                  console.log('Image loaded successfully for product:', product.name, 'URL:', product.images?.[0]);
                }}
              />
            </div>
            <div className="product-info">
              <h3>{product.name}</h3>
              <p className="price">${product.price.toFixed(2)}</p>
              <p className="stock">Stock: {product.stock_quantity}</p>
              <p className={`status ${product.is_active ? 'active' : 'inactive'}`}>
                {product.is_active ? 'Active' : 'Inactive'}
              </p>
            </div>
            <div className="product-actions">
              <button onClick={() => handleEditProduct(product)}>Edit</button>
              <button
                onClick={() => handleDeleteProduct(product.id)}
                className="delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showProductForm && (
        <div className="modal-overlay" onClick={() => {
          cleanupTempFiles();
          setShowProductForm(false);
        }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  cleanupTempFiles();
                  setShowProductForm(false);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleSaveProduct(); }}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Price *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={productForm.price}
                      onChange={(e) => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Stock Quantity</label>
                    <input
                      type="number"
                      value={productForm.stock_quantity}
                      onChange={(e) => setProductForm(prev => ({ ...prev, stock_quantity: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Size</label>
                    <input
                      type="text"
                      value={productForm.size}
                      onChange={(e) => setProductForm(prev => ({ ...prev, size: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label>Category</label>
                    <input
                      type="text"
                      value={productForm.category}
                      onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Images</label>
                  {productForm.images.map((image, index) => {
                    const uploadState = uploadStates[index];
                    return (
                      <div key={index} className="image-upload-group">
                        <div 
                          className={`image-upload-area ${uploadState?.isUploading ? 'uploading' : ''}`}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(index, e)}
                        >
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleDirectUpload(index, file);
                            }}
                            className="file-input"
                          />
                          <div className="upload-instruction">
                            <span>Drag & drop an image here or click to browse</span>
                            <span className="file-size-limit">Max 5MB (JPEG, PNG, GIF)</span>
                          </div>
                        </div>
                        
                        {image && (
                          <div className="image-preview">
                            <div className="thumbnail-container">
                              <img 
                                src={image} 
                                alt={`Product ${index + 1}`}
                                className="thumbnail-image"
                                onClick={() => {
                                  setSelectedImage(image);
                                  setShowImageModal(true);
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => updateImageField(index, '')}
                                className="remove-image-btn"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )}

                        {uploadState && (
                          <div className="upload-status">
                            {uploadState.isUploading && (
                              <div className="upload-progress">
                                <div className="progress-bar">
                                  <div 
                                    className="progress-fill" 
                                    style={{ width: `${uploadState.progress}%` }}
                                  ></div>
                                </div>
                                <span>Uploading...</span>
                              </div>
                            )}
                            {uploadState.error && (
                              <div className="upload-error">{uploadState.error}</div>
                            )}
                            {uploadState.progress === 100 && !uploadState.error && (
                              <div className="upload-success">Upload complete!</div>
                            )}
                          </div>
                        )}

                        {productForm.images.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeImageField(index)}
                            className="remove-image-field-btn"
                          >
                            Remove Image Field
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={addImageField}
                    className="add-image-btn"
                  >
                    Add Another Image
                  </button>
                </div>

                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input
                    type="text"
                    value={productForm.tags}
                    onChange={(e) => setProductForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="vintage, 1990s, jeans"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => {
                    cleanupTempFiles();
                    setShowProductForm(false);
                  }}>
                    Cancel
                  </button>
                  <button type="submit" className="save-btn">
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Full-size Image Modal */}
      {showImageModal && (
        <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal-content" onClick={e => e.stopPropagation()}>
            <button
              className="image-modal-close"
              onClick={() => setShowImageModal(false)}
            >
              ×
            </button>
            <div className="image-modal-body">
              <img src={selectedImage} alt="Full size preview" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManager;
